import type { PermissionModule } from './permissionsApi';

/**
 * The product modules of Firmio: what the Firmio Console sells per customer
 * and what Settings → Module switches on or off.
 *
 * MIRROR of backend/src/lib/moduleCatalog.ts, the source of truth; keep in
 * sync (backend/test/moduleCatalogMirrors.test.ts checks). The effects are
 * here too because the admin bypass in usePermissions never reads the matrix
 * the server masked, so it has to know which flags an off module takes away.
 */
export type ModuleKey =
  | 'dashboard'
  | 'dailySummary'
  | 'weeklySummary'
  | 'aiAssistant'
  | 'mcp'
  | 'videoMeetings'
  | 'teamChat'
  | 'reminders'
  | 'leave'
  | 'tasks'
  | 'calendar'
  | 'taskReports'
  | 'meetings'
  | 'leads'
  | 'projects'
  | 'clients'
  | 'clientTasks'
  | 'clientTeam'
  | 'socialPosts'
  | 'suppliers'
  | 'subscriptions'
  | 'financials'
  | 'invoices'
  | 'spv'
  | 'bankStatements'
  | 'cashflow'
  | 'monthlyReports'
  | 'actors'
  | 'whatsapp';

export interface ModuleDef {
  key: ModuleKey;
  label: string;
  parent?: ModuleKey;
  core?: boolean;
  /** Built for one customer: off everywhere until granted (see backend lib/modules.ts). */
  optIn?: boolean;
  masks?: readonly PermissionModule[];
  maskFlags?: Partial<Record<PermissionModule, readonly string[]>>;
}

export const MODULE_CATALOG: readonly ModuleDef[] = [
  { key: 'dashboard', label: 'Dashboard', masks: ['dashboard'] },
  { key: 'dailySummary', label: 'Rezumat zilnic', masks: ['dailySummary'] },
  { key: 'weeklySummary', label: 'Rezumat săptămânal', masks: ['weeklySummary'] },
  { key: 'aiAssistant', label: 'Firmio AI', masks: ['aiAssistant'] },
  { key: 'mcp', label: 'Firmio MCP', parent: 'aiAssistant' },
  { key: 'videoMeetings', label: 'Firmio Meet', masks: ['videoMeetings'] },
  { key: 'teamChat', label: 'Chat echipă' },
  { key: 'reminders', label: 'Remindere' },
  { key: 'leave', label: 'Concedii', masks: ['leave'] },
  { key: 'tasks', label: 'Sarcini', core: true },
  { key: 'calendar', label: 'Calendar', parent: 'tasks' },
  {
    key: 'taskReports',
    label: 'Rapoarte sarcini',
    parent: 'tasks',
    maskFlags: { tasks: ['canViewReports'] },
  },
  { key: 'meetings', label: 'Întâlniri', masks: ['meetings'] },
  { key: 'leads', label: 'Pipeline', masks: ['leads'] },
  { key: 'projects', label: 'Proiecte', core: true },
  { key: 'clients', label: 'Clienți', masks: ['clients'] },
  { key: 'clientTasks', label: 'Sarcini', parent: 'clients' },
  { key: 'clientTeam', label: 'Echipă', parent: 'clients' },
  { key: 'socialPosts', label: 'Calendar postări', parent: 'clients', masks: ['socialPosts'] },
  { key: 'suppliers', label: 'Furnizori', masks: ['suppliers'] },
  { key: 'subscriptions', label: 'Abonamente', masks: ['subscriptions'] },
  { key: 'financials', label: 'Financiar', masks: ['financials'] },
  {
    key: 'invoices',
    label: 'Facturi',
    parent: 'financials',
    maskFlags: {
      financials: [
        'canViewInvoices',
        'canCreateInvoices',
        'canEditInvoices',
        'canDeleteInvoices',
        'canViewSmartBill',
        'canSyncSmartBill',
        'canAssociateSmartBill',
        'canIssueInvoices',
        'canApproveInvoices',
        'canSendReminders',
      ],
    },
  },
  {
    key: 'spv',
    label: 'SPV',
    parent: 'financials',
    maskFlags: { financials: ['canViewSpv', 'canSyncSpv'] },
  },
  {
    key: 'bankStatements',
    label: 'Extrase bancare',
    parent: 'financials',
    maskFlags: {
      financials: ['canViewBankStatements', 'canUploadBankStatements', 'canReconcile'],
    },
  },
  {
    key: 'cashflow',
    label: 'Cashflow',
    parent: 'financials',
    maskFlags: { financials: ['canManageScheduledPayments'] },
  },
  { key: 'monthlyReports', label: 'Rapoarte lunare', parent: 'financials' },
  { key: 'actors', label: 'Actori', masks: ['actors'], optIn: true },
  { key: 'whatsapp', label: 'WhatsApp', masks: ['whatsapp'], optIn: true },
];

/** True when an off module takes this permission flag away (see usePermissions). */
export function isFlagMasked(
  off: ReadonlySet<ModuleKey>,
  module: PermissionModule,
  action: string,
): boolean {
  for (const def of MODULE_CATALOG) {
    if (!off.has(def.key)) continue;
    if (def.masks?.includes(module)) return true;
    if (def.maskFlags?.[module]?.includes(action)) return true;
  }
  return false;
}
